import { windowFailureHint } from '@lume/shared';
import { Modal } from '@lume/ui-components';
import { useState } from 'react';

import type { MessageItem } from '../../../lib/types';

/**
 * MessageFailedModal — r7b §30.2 ADR-113.
 *
 * Modal de detalhes para mensagem com falha de envio.
 * Exibe:
 *   - Texto da mensagem (editável para reenvio)
 *   - Motivo da falha (`failureReason`)
 *   - Contexto de auditoria (ADR-113 — `message.send.failed`)
 *
 * Ações r7b §30.2:
 *   - "Editar antes de reenviar" — abre campo de edição de texto
 *   - "Tentar novamente" — reenvia com o texto original (ou editado)
 *   - "Fechar"
 *
 * Consume `Modal` do DS v2.2.0 (`@lume/ui-components`).
 */
export function MessageFailedModal({
  open,
  message,
  onClose,
  onRetry,
}: {
  open: boolean;
  message: MessageItem | null;
  onClose: () => void;
  onRetry?: (message: MessageItem) => void;
}): JSX.Element | null {
  const [editMode, setEditMode] = useState(false);
  const [editedContent, setEditedContent] = useState('');

  if (!open || message === null) return null;

  const handleRetry = () => {
    if (onRetry !== undefined) {
      const msgToRetry: MessageItem = editMode
        ? { ...message, content: editedContent || message.content }
        : message;
      onRetry(msgToRetry);
    }
    onClose();
  };

  const handleEditBeforeRetry = () => {
    setEditedContent(message.content);
    setEditMode(true);
  };

  const confirmProps =
    onRetry !== undefined
      ? {
          confirmLabel: 'Tentar novamente',
          onConfirm: handleRetry,
        }
      : {};

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Mensagem com falha de envio"
      {...confirmProps}
      cancelLabel="Fechar"
      maxWidth="480px"
    >
      <div className="space-y-3 text-sm">
        <div>
          <div className="flex items-center justify-between mb-1">
            <p className="text-2xs uppercase tracking-wide font-semibold text-text-secondary">
              Texto da mensagem
            </p>
            {onRetry !== undefined && !editMode && (
              <button
                type="button"
                onClick={handleEditBeforeRetry}
                className="text-2xs text-text-secondary underline hover:text-text-primary focus:outline-none"
              >
                Editar antes de reenviar
              </button>
            )}
          </div>
          {editMode ? (
            <textarea
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              className="input w-full resize-none text-sm"
              rows={3}
              autoFocus
              aria-label="Editar texto da mensagem antes de reenviar"
            />
          ) : (
            <div className="bg-surface-raised rounded px-3 py-2 text-sm text-text-primary">
              &ldquo;{message.content}&rdquo;
            </div>
          )}
        </div>

        <div>
          <p className="text-2xs uppercase tracking-wide font-semibold text-text-secondary mb-1">
            Motivo da falha
          </p>
          {/* Fase C Brief 2A r7b — DS canonical tokens (Spec §1.5 .confirm-modal--danger). */}
          <p
            className="text-xs font-mono rounded px-2 py-1.5"
            style={{ color: 'var(--danger-fg)', background: 'var(--danger-bg)' }}
          >
            {message.failureReason ?? 'Sem motivo registrado'}
          </p>
          {windowFailureHint(message.failureReason) !== null && (
            <p className="text-xs mt-1" style={{ color: 'var(--fg-2)' }}>
              {windowFailureHint(message.failureReason)}
            </p>
          )}
        </div>

        {(message.retryAttempt ?? 0) > 0 && (
          <div>
            <p className="text-2xs uppercase tracking-wide font-semibold text-text-secondary mb-1">
              Tentativas
            </p>
            <p className="text-xs text-text-secondary">
              {message.retryAttempt ?? 0} de {message.retryMax ?? '?'} tentativa(s) esgotada(s).
            </p>
          </div>
        )}

        <p className="text-2xs font-mono text-text-secondary opacity-60">
          audit: message.send.failed · ADR-113
        </p>
      </div>
    </Modal>
  );
}
